// The one shape Quick view is built from: a storyboard panel. A blank panel is where you write what to draw, the same
// panel fills with hatching while it is drawn, and the drawing lands in it. Viewfinder corners mark a panel waiting for
// something (words, or your voice while recording).
import { useEffect, useState, type ReactNode } from "react";

export function Panel({ children, corners, rec, blank, className, onClick, label }: {
  children?: ReactNode; corners?: boolean; rec?: boolean; blank?: boolean; className?: string; onClick?: () => void; label?: string;
}) {
  const cls = `sm-panel${blank ? " blank" : ""}${rec ? " rec" : ""}${className ? ` ${className}` : ""}`;
  const inner = <>{children}{(corners || rec) && <i className="sm-corners" aria-hidden />}</>;
  return onClick ? <button className={cls} onClick={onClick} aria-label={label}>{inner}</button> : <div className={cls}>{inner}</div>;
}

const PAINT_MS = 20_000;

// Inside a panel while a picture is drawn: moving pencil hatching, what is being drawn, and a bar that fills towards 90%
// across 20 seconds, then waits. No times or numbers.
export function Drawing({ message, since }: { message: string; since: number | null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 200); return () => clearInterval(t); }, []);
  const x = Math.min(1, Math.max(0, now - (since ?? now)) / PAINT_MS);
  const pct = Math.round(90 * (1 - (1 - x) ** 2));
  return (
    <div className="sm-drawing" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={message}>
      <div className="sm-hatch" aria-hidden />
      <div className="sm-drawing-foot">
        <b>{message}</b>
        <div className="sm-bar"><span style={{ width: `${pct}%` }} /></div>
      </div>
    </div>
  );
}

// What went wrong with a drawing, in words that say what to do. The drawing service's safety filter is the usual cause.
export function paintProblem(message: string): string {
  if (/content_policy|flagged|content checker|safety|moderation/i.test(message))
    return "The drawing service refused those words. Try describing it differently: it often blocks famous characters, real people and anything violent.";
  return "The drawing didn't work this time. Check your words and try again.";
}

// A problem shown inside the panel, until the next try.
export const Problem = ({ children }: { children: ReactNode }) => <p className="sm-problem" role="alert">{children}</p>;
